import { useState, useEffect, useRef } from 'react'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Slider } from '@/components/ui/slider'
import { Card } from '@/components/ui/card'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { toast } from 'sonner'
import { User, Sparkle, DiceThree, Crown, Ghost, Camera, Trash } from '@phosphor-icons/react'
import { Loader2 } from 'lucide-react'
import type { Character, GameConfig } from '@/types'
import { apiService } from '@/services/api'
import { sortearAtributos, pontosUsados, clampComOrcamento } from '@/lib/sortearAtributos'
import { ImageCropDialog } from '@/components/account/ImageCropDialog'

const MAX_IMAGE_SIZE = 10 * 1024 * 1024 // 10MB para imagem original
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif']

interface CreateCharacterDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreateCharacter: (character: Omit<Character, 'id' | 'tableId' | 'userId'>, imageFile?: File) => void
  tableId: string
  isMaster?: boolean
}

export function CreateCharacterDialog({ open, onOpenChange, onCreateCharacter, tableId, isMaster = false }: CreateCharacterDialogProps) {
  
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [className, setClassName] = useState('')
  const [isNpc, setIsNpc] = useState(false)
  const [level] = useState(1)
  const [gameConfig, setGameConfig] = useState<GameConfig | null>(null)
  const [dynamicAttributes, setDynamicAttributes] = useState<Record<string, number>>({})
  const [loadingConfig, setLoadingConfig] = useState(false)
  
  // Estados para imagem
  const [imageFile, setImageFile] = useState<File | null>(null)
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [showCropDialog, setShowCropDialog] = useState(false)
  const [cropImageSrc, setCropImageSrc] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (open && tableId) {
      fetchGameConfig()
    }
  }, [open, tableId])

  // Carregar dados do personagem quando em modo de edição
  useEffect(() => {
    console.log('🎮 CreateCharacterDialog - useEffect executado:', { 
      open, 
      hasGameConfig: !!gameConfig
    });
    
    if (open && gameConfig) {
      // Modo de criação - limpar campos e usar valores padrão dos atributos
      console.log('🎮 CreateCharacterDialog - MODO CRIAÇÃO - Limpando campos');
      setName('')
      setDescription('')
      setClassName('')
      setIsNpc(false)
      setImageFile(null)
      setImagePreview(null)
      
      // Inicializar com valores padrão do gameConfig
      if (gameConfig?.attributes) {
        const initialAttrs: Record<string, number> = {}
        gameConfig.attributes.forEach(attr => {
          initialAttrs[attr.id] = attr.default
        })
        setDynamicAttributes(initialAttrs)
      }
    } else {
      console.log('🎮 CreateCharacterDialog - Aguardando gameConfig');
    }
  }, [open, gameConfig])

  const fetchGameConfig = async () => {
    setLoadingConfig(true)
    try {
      console.log('🎮 CreateCharacterDialog - Fetching game_config for tableId:', tableId)
      
      const config = await apiService.getGameConfig(tableId)
      
      console.log('🎮 Game config loaded:', config)
      console.log('🎮 Classes count:', config?.classes?.length)
      setGameConfig(config)
    } catch (error) {
      console.error('❌ Erro ao carregar game_config:', error)
      toast.error('Não foi possível carregar as configurações da campanha')
    } finally {
      setLoadingConfig(false)
    }
  }

  // Sorteio respeitando o orcamento da classe (starting_points) — espelha o
  // sorteio server-side. Sem classe selecionada o botao fica desabilitado
  // (o orcamento e da classe; nao ha fallback silencioso).
  const handleRandomize = () => {
    if (!gameConfig?.attributes || !className) return
    const startingPoints =
      gameConfig.classes?.find(c => c.name === className)?.starting_points ?? 10
    setDynamicAttributes(sortearAtributos(gameConfig.attributes, startingPoints))
  }

  // Fase 2 — teto na criacao manual (jogador; o mestre fica livre): orcamento
  // da classe selecionada, contador usados/disponiveis e trava dos sliders.
  const orcamento = !isMaster && className
    ? gameConfig?.classes?.find(c => c.name === className)?.starting_points ?? 10
    : null
  const usados = gameConfig?.attributes ? pontosUsados(gameConfig.attributes, dynamicAttributes) : 0
  const estourado = orcamento !== null && usados > orcamento

  const handleAttributeChange = (attrId: string, requested: number) => {
    if (!gameConfig?.attributes) return
    const value = clampComOrcamento(gameConfig.attributes, dynamicAttributes, attrId, requested, orcamento)
    setDynamicAttributes(prev => ({ ...prev, [attrId]: value }))
  }

  // Trocar de classe reseta os atributos para os defaults (o orcamento e da
  // classe; re-acomodar valores da classe anterior seria imprevisivel).
  useEffect(() => {
    if (!open || !gameConfig?.attributes || !className) return
    const initialAttrs: Record<string, number> = {}
    gameConfig.attributes.forEach(attr => {
      initialAttrs[attr.id] = attr.default
    })
    setDynamicAttributes(initialAttrs)
  }, [className])

  const handleImageClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validar tipo
    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error('Tipo de arquivo não permitido. Use JPG, PNG, WebP ou GIF.')
      return
    }

    // Validar tamanho
    if (file.size > MAX_IMAGE_SIZE) {
      toast.error('Arquivo muito grande. Máximo permitido: 10MB')
      return
    }

    // Criar URL para o cropper
    const reader = new FileReader()
    reader.onload = (e) => {
      setCropImageSrc(e.target?.result as string)
      setShowCropDialog(true)
    }
    reader.readAsDataURL(file)
    
    // Limpar input para permitir selecionar o mesmo arquivo novamente
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleCropComplete = (croppedFile: File) => {
    // Criar preview da imagem recortada
    const reader = new FileReader()
    reader.onload = (e) => {
      setImagePreview(e.target?.result as string)
    }
    reader.readAsDataURL(croppedFile)
    
    setImageFile(croppedFile)
    setCropImageSrc(null)
  }

  const handleRemoveImage = () => {
    setImageFile(null)
    setImagePreview(null)
  }

  const getInitials = (characterName: string) => {
    return characterName.split(' ').map(word => word[0]).join('').toUpperCase().slice(0, 2) || '?'
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!name.trim() || !className) {
      toast.error('Preencha nome e classe')
      return
    }

    // HP inicial da mesa (fase 3 do plano de feedback; default 20)
    const hpTotal = gameConfig?.initial_hp ?? 20

    onCreateCharacter({
      name: name.trim(),
      className: className,
      level,
      hpCurrent: hpTotal,
      hpTotal: hpTotal,
      points: 0,
      description: description.trim(),
      imageUrl: undefined, // Será preenchido após upload
      attributes: dynamicAttributes,
      isNpc: isNpc
    }, imageFile || undefined)

    // Limpar os campos após criar
    setName('')
    setDescription('')
    setClassName('')
    setIsNpc(false)
    setDynamicAttributes({})
    setImageFile(null)
    setImagePreview(null)
    onOpenChange(false)
  }

  const availableClasses = gameConfig?.classes || []

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">
            Criar Personagem
          </DialogTitle>
          <DialogDescription>
            Crie seu personagem para esta campanha
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Foto do Personagem */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Camera weight="fill" size={18} />
              Foto do Personagem (Opcional)
            </Label>
            <div className="flex items-center gap-4">
              <div className="relative">
                <Avatar 
                  className="h-24 w-24 cursor-pointer border-2 border-dashed border-muted-foreground/50 hover:border-primary transition-colors" 
                  onClick={handleImageClick}
                >
                  {imagePreview ? (
                    <AvatarImage src={imagePreview} alt="Preview" />
                  ) : null}
                  <AvatarFallback className="text-3xl bg-muted/50">
                    {name ? getInitials(name) : <Camera size={32} className="text-muted-foreground" />}
                  </AvatarFallback>
                </Avatar>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  onChange={handleFileChange}
                  className="hidden"
                />
              </div>
              <div className="flex flex-col gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleImageClick}
                >
                  <Camera size={16} className="mr-2" />
                  {imagePreview ? 'Trocar foto' : 'Selecionar foto'}
                </Button>
                {imagePreview && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleRemoveImage}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash size={16} className="mr-2" />
                    Remover
                  </Button>
                )}
                <p className="text-xs text-muted-foreground">
                  JPG, PNG, WebP ou GIF. Máx 10MB.
                </p>
              </div>
            </div>
          </div>
          
          {/* Dialog de Crop */}
          <ImageCropDialog
            open={showCropDialog}
            onOpenChange={setShowCropDialog}
            imageSrc={cropImageSrc || ''}
            onCropComplete={handleCropComplete}
          />

          {/* Nome */}
          <div className="space-y-2">
            <Label htmlFor="name" className="flex items-center gap-2">
              <User weight="fill" size={18} />
              Nome do Personagem
            </Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex: Aria Valorosa"
              required
            />
          </div>

          {/* Tipo de Personagem (apenas para mestre) */}
          {isMaster && (
            <div className="space-y-2">
              <Label htmlFor="characterType" className="flex items-center gap-2">
                <Crown weight="fill" size={18} />
                Tipo de Personagem
              </Label>
              <Select value={isNpc ? 'npc' : 'master'} onValueChange={(value) => setIsNpc(value === 'npc')}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="master">
                    <div className="flex items-center gap-2">
                      <Crown weight="fill" size={16} />
                      <span>Mestre</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="npc">
                    <div className="flex items-center gap-2">
                      <Ghost weight="fill" size={16} />
                      <span>NPC</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Classe */}
          <div className="space-y-2">
            <Label htmlFor="class" className="flex items-center gap-2">
              <Sparkle weight="fill" size={18} />
              Classe
            </Label>
            {availableClasses.length === 0 ? (
              <div className="border border-dashed rounded-md p-4 text-center text-sm text-muted-foreground">
                <p className="font-medium">Nenhuma classe configurada</p>
                <p className="text-xs mt-1">O mestre da campanha precisa configurar as classes na aba "Classes"</p>
              </div>
            ) : (
              <Select value={className} onValueChange={setClassName} required>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione a classe" />
                </SelectTrigger>
                <SelectContent>
                  {availableClasses.map((cls) => (
                    <SelectItem key={cls.id} value={cls.name}>
                      <span className="flex items-center gap-2">
                        <span>{cls.icon}</span>
                        <span>{cls.name}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Atributos */}
          {loadingConfig ? (
            <div className="text-center py-4 text-muted-foreground">
              Carregando atributos...
            </div>
          ) : gameConfig?.attributes ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Label className="text-base font-semibold">Atributos</Label>
                  {orcamento !== null && (
                    <span
                      className={
                        estourado
                          ? 'text-sm font-semibold text-destructive'
                          : 'text-sm font-medium text-muted-foreground'
                      }
                    >
                      Pontos: {usados} / {orcamento}
                    </span>
                  )}
                </div>
                <Button
                  type="button"
                  onClick={handleRandomize}
                  variant="outline"
                  size="sm"
                  disabled={!className}
                  title={!className ? 'Escolha uma classe primeiro' : undefined}
                >
                  <DiceThree weight="fill" className="mr-2" size={18} />
                  Aleatorizar
                </Button>
              </div>
              {estourado && (
                <p className="text-sm text-destructive">
                  A distribuição excede os pontos da classe — reduza atributos
                  até caber no orçamento.
                </p>
              )}

              <div className="grid gap-4">
                {gameConfig.attributes.map((attr) => (
                  <Card key={attr.id} className="p-4">
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <Label className="font-semibold">{attr.name}</Label>
                        <span className="text-2xl font-bold text-primary">
                          {dynamicAttributes[attr.id] || attr.default}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground">{attr.description}</p>
                      <Slider
                        value={[dynamicAttributes[attr.id] || attr.default]}
                        onValueChange={(val) => handleAttributeChange(attr.id, val[0])}
                        min={attr.min}
                        max={attr.max}
                        step={1}
                      />
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>Min: {attr.min}</span>
                        <span>Max: {attr.max}</span>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          ) : null}

          {/* Descrição */}
          <div className="space-y-2">
            <Label htmlFor="description">Descrição (Opcional)</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="História e características do personagem..."
              rows={3}
            />
          </div>

          {/* Botões */}
          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="flex-1">
              Cancelar
            </Button>
            <Button
              type="submit"
              className="flex-1"
              disabled={availableClasses.length === 0 || !className || estourado}
            >
              Criar Personagem
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
